import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { COMPASS_IMG } from "@/lib/api";

const AuthPage = () => {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [submitting, setSubmitting] = useState(false);
  const isLogin = mode === "login";
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Welcome back aboard");
      } else {
        await signup(form.name, form.email, form.password, form.referral_code);
        toast.success("Your voyage begins");
      }
      navigate("/community");
    } catch (err) {
      const msg = err?.response?.data?.detail || (isLogin ? "Invalid credentials" : "Could not create account");
      toast.error(typeof msg === "string" ? msg : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "mt-1.5 w-full px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background sea-gradient">
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-border overflow-hidden">
        <div className="absolute inset-0 sea-gradient" />
        <div className="absolute -right-24 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-primary/10 blur-3xl" />

        <div className="relative z-10 flex items-center gap-2.5 text-foreground">
          <img src={COMPASS_IMG} alt="The Daily Compass" className="w-10 h-10 rounded-full object-cover compass-glow" />
          <span className="font-heading text-2xl font-semibold">The Daily Compass</span>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="relative mb-9">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl scale-110" />
            <img
              data-testid="auth-hero-compass"
              src={COMPASS_IMG}
              alt="Glowing compass"
              className="relative w-64 h-64 rounded-full object-cover compass-spin-slow compass-glow"
            />
          </div>
          <h1 className="font-heading text-4xl font-semibold leading-tight text-foreground max-w-md">
            Find your bearings, one day at a time.
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">
            Daily devotionals, honest journaling, recovery programs, and a community
            that keeps you steady through every tide.
          </p>
        </div>

        <p className="relative z-10 text-sm text-brass/80 font-heading italic text-center">His Words. Our Work. Greater Things.</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src={COMPASS_IMG} alt="The Daily Compass" className="w-10 h-10 rounded-full object-cover compass-glow" />
            <span className="font-heading text-2xl font-semibold text-foreground">The Daily Compass</span>
          </div>
          <h2 className="font-heading text-3xl font-semibold text-foreground">
            {isLogin ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-muted-foreground mt-2 mb-8">
            {isLogin ? "Sign in to continue your journey." : "Join a crew that has your back."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm font-medium">Name</label>
                <input data-testid="signup-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={input} />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Email</label>
              <input data-testid="auth-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={input} />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input data-testid="auth-password-input" required type="password" value={form.password} onChange={update("password")} placeholder="••••••••" className={input} />
            </div>
            {!isLogin && (
              <div>
                <label className="text-sm font-medium">Referral code <span className="text-muted-foreground">(optional)</span></label>
                <input data-testid="signup-referral-input" value={form.referral_code} onChange={update("referral_code")} placeholder="e.g. 4BECSSSG" className={input} />
              </div>
            )}
            <button data-testid="auth-submit-button" type="submit" disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-3 rounded-full hover:bg-atlantic active:scale-[0.98] transition-all disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLogin ? "Sign in" : "Create account"}
            </button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isLogin ? "New to The Daily Compass?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(isLogin ? "signup" : "login")} className="text-primary font-semibold hover:underline">
              {isLogin ? "Create one" : "Sign in"}
            </button>
          </p>
          <div className="mt-8 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground">Privacy</a>
            <a href="/terms" className="hover:text-foreground">Terms</a>
            <a href="/support" className="hover:text-foreground">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
